<template>
  <v-container>
    <v-card :elevation="10">
      <v-card-title>
        <h3>
          対象物件情報の入力
        </h3>
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>委託業務名</h3>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.title"
              v-bind="diffAttributes(previousObject.title, form.title)"
              outlined
              label="委託業務名"
              :error-messages="error.title"
              dense />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>施設名</h3>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.facility_name"
              v-bind="diffAttributes(previousObject.facility_name, form.facility_name)"
              outlined
              label="施設名"
              :error-messages="error.facility_name"
              dense />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>所在地</h3>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.address"
              v-bind="diffAttributes(previousObject.address, form.address)"
              outlined
              label="所在地"
              :error-messages="error.address"
              dense />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>分類</h3>
          </v-col>
          <v-col cols="3">
            <v-radio-group
              v-model="form.category"
              v-bind="diffAttributes(previousObject.category, form.category, {1: '建築物', 2: '工作物'})"
              row>
              <v-radio
                label="建築物"
                :value="1" />
              <v-radio
                label="工作物"
                :value="2" />
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row
          v-if="form.category === 2"
          dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>設置場所</h3>
          </v-col>
          <v-col cols="3">
            <v-radio-group
              v-model="form.placement"
              v-bind="diffAttributes(previousObject.placement, form.placement, {1: '屋内', 2: '屋外'})">
              <v-radio
                class="d-flex"
                label="屋内"
                :value="1" />
              <v-radio
                class="d-flex"
                label="屋外"
                :value="2" />
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row
          v-else
          dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>建物情報</h3>
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="form.floors"
              v-bind="diffAttributes(previousObject.floors, form.floors)"
              label="階数(数字を入力してください)"
              :items="floors"
              :error-messages="error.floors"
              menu-props="auto"
              dense
              outlined />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="form.floor_space"
              v-bind="diffAttributes(previousObject.floor_space, form.floor_space)"
              outlined
              label="延床面積"
              :error-messages="error.floor_space"
              suffix="㎡"
              dense />
          </v-col>
        </v-row>

        <v-row
          v-if="form.category === 1"
          dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>建物構造（複数選択可）</h3>
          </v-col>
          <v-col
            cols="4"
            v-bind="diffAttributes(previousObject.structures, form.structures)">
            <template v-for="(structure, index) in structures">
              <v-checkbox
                :key="index"
                v-model="form.structures"
                class="ma-0 pt-0 pr-5"
                dense
                :error="!!error.structures"
                :label="structure.name"
                :value="structure.id" />
              <v-text-field
                v-if="structure.has_other"
                :key="`field-${index}`"
                v-model="form.structures_other_text"
                v-bind="diffAttributes(
                  previousObject.structures_other_text, form.structures_other_text)"
                :disabled="!hasOtherStructure"
                class="ml-5"
                dense
                outlined
                label="建物構造（その他）" />
            </template>
            <v-alert
              v-if="error.structures"
              dense
              outlined
              type="error">
              {{ error.structures[0] }}
            </v-alert>
            <template v-if="hasDiff(previousObject.structures, form.structures)">
              変更前:
              <span
                v-for="(structure, index) in previousObject.structures"
                :key="`pst-${index}`">
                {{ structure.name }}
              </span>
            </template>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>建物用途（複数選択可）</h3>
          </v-col>
          <v-col
            cols="4"
            v-bind="diffAttributes(previousObject.features, form.features)">
            <template v-for="(feature, index) in features">
              <div
                v-if="feature.category === form.category"
                :key="index">
                <v-checkbox
                  v-model="form.features"
                  class="ma-0 pt-0 pr-5"
                  dense
                  :error="!!error.features"
                  :label="feature.name"
                  :value="feature.id" />
                <v-text-field
                  v-if="feature.has_other"
                  :key="`field-${index}`"
                  v-model="form.features_other_text"
                  :disabled="!hasOtherFeature"
                  class="ml-5"
                  dense
                  outlined
                  label="建物用途（特殊建築物）"
                  v-bind="diffAttributes(
                    previousObject.features_other_text, form.features_other_text)" />
              </div>
            </template>
            <v-alert
              v-if="error.features"
              dense
              outlined
              type="error">
              {{ error.features[0] }}
            </v-alert>
            <template v-if="hasDiff(previousObject.features, form.features)">
              変更前:
              <span
                v-for="(feature, index) in previousObject.features"
                :key="`pst-${index}`">
                {{ feature.name }}
              </span>
            </template>
          </v-col>
        </v-row>
      </v-card-text>
      <slot name="footer">
        <global-footer>
          <template #left>
            <v-btn
              large
              @click.prevent="onReturnClicked">
              <v-icon>mdi-chevron-left</v-icon>
              戻る
            </v-btn>
          </template>
          <template #right>
            <v-btn
              v-if="!form.readonly"
              large
              color="default"
              class="mr-1"
              :disabled="!hasChanges"
              @click.prevent="onDraftSaveClicked">
              ブラウザに一時保存
            </v-btn>
            <v-btn
              large
              color="primary"
              @click.prevent="onNextClicked">
              次へ
              <v-icon right>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
        </global-footer>
      </slot>
    </v-card>
  </v-container>
</template>

<script>
import {
  getStepSession, markAsUnsavedSession, setStepSession,
} from '../../logic/session'
import PreviousDiff from '../../mixins/PreviousDiff'

export default {
  name: 'ConfirmationFormStep1',
  mixins: [PreviousDiff],
  props: {
    isNew: {
      type: Boolean,
      default: false,
    },
    structures: {
      type: Array,
      default: () => ([]),
    },
    features: {
      type: Array,
      default: () => ([]),
    },
    created: {
      type: Boolean,
      default: false,
    },
    applied: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        title: '',
        facility_name: '',
        type: '1',
        address: '',
        category: 1,
        floors: 1,
        floor_space: '',
        structures: [],
        structures_other_text: '',
        features: [],
        features_other_text: '',
      },
      error: {},
      hasChanges: false,
    }
  },
  computed: {
    floors() {
      const floors = []
      for (let i = -10; i <= 30; i += 1) {
        if (i !== 0) {
          floors.push({
            text: (i > 0) ? `${i}F` : `B${Math.abs(i)}`,
            value: i,
          })
        }
      }
      return floors
    },
    hasOtherStructure() {
      return this.structures.filter(
        structure => (this.form.structures.indexOf(structure.id) !== -1 && structure.has_other),
      ).length > 0
    },
    hasOtherFeature() {
      return this.features.filter(
        feature => (this.form.features.indexOf(feature.id) !== -1 && feature.has_other),
      ).length > 0
    },
    previousCategory() {
      if (this.edited('category')) {
        switch (this.previousObject.category) {
          case 1:
            return '変更前: 建築物'
          case 2:
            return '変更前: 工作物'
          default:
            return ''
        }
      }
      return ''
    },
    previousPlacement() {
      if (this.edited('placement')) {
        switch (this.previousObject.placement) {
          case 1:
            return '変更前: 屋内'
          case 2:
            return '変更前: 屋外'
          default:
            return ''
        }
      }
      return ''
    },
  },
  watch: {
    hasChanges() {
      this.$emit('hasChange', this.hasChanges)
    },
  },
  mounted() {
    if (!this.isNew) {
      this.fetchItem()
    }
    this.loadItem()
  },

  methods: {
    loadItem() {
      const form = getStepSession('confirmation', 1)
      if (form) {
        this.form = form
      }
      // 変更があればフラグをセット
      this.$watch('form', () => {
        this.hasChanges = true
      }, { deep: true })
    },
    async fetchItem() {
      return true
    },
    async validateItem() {
      try {
        await window.axios.post('/api/confirmations/step1', this.form)
        this.error = {}
        return true
      } catch (e) {
        if (e.response.status === 422) {
          this.error = e.response.data.errors
        }
      }
      return false
    },
    onReturnClicked() {
      if (this.isNew) {
        this.$router.push({ name: 'confirmationNewStep0' })
      } else {
        this.$router.push({ name: 'confirmationIndex' })
      }
    },
    async onNextClicked() {
      if (this.form.readonly) {
        this.$emit('change', 2)
        return
      }
      if (await this.validateItem()) {
        if (this.form.category === 2) {
          this.form.floors = 1
        }
        if (this.hasChanges) {
          markAsUnsavedSession('confirmation')
        }
        setStepSession('confirmation', 1, this.form)
        this.$emit('change', 2)
      }
    },
    onDraftSaveClicked() {
      setStepSession('confirmation', 1, this.form)
      markAsUnsavedSession('confirmation')
      this.hasChanges = false
    },
    diffAttributes(previous, current, options = null) {
      return this.created && this.applied
        ? this.$super(PreviousDiff).diffAttributes(previous, current, options)
        : false
    },
    hasDiff(previous, current) {
      return this.created && this.applied
        ? this.$super(PreviousDiff).hasDiff(previous, current)
        : false
    },
  },
}
</script>
